"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import { AgentWidgetAPI } from "../api"
import { useVoice } from "./useVoice"
import type { Message, User, Product, Order } from "../types"

export function useChat(apiKey: string, agentId: string, baseUrl: string, initialUser?: User, voiceEnabled = false, onUserUpdate?: (user: User) => void) {
    const [messages, setMessages] = useState<Message[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [conversationId, setConversationId] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    // Load user from localStorage or use initialUser
    const [user, setUser] = useState<User | null>(() => {
        if (initialUser) {
            console.log('Using initial user:', initialUser)
            return initialUser
        }

        if (typeof window !== "undefined") {
            const key = `agent_user_${agentId}`
            const stored = localStorage.getItem(key)
            console.log('Checking localStorage for key:', key)
            console.log('Stored value:', stored)
            if (stored) {
                try {
                    const parsed = JSON.parse(stored)
                    console.log('Parsed user from localStorage:', parsed)
                    return parsed
                } catch (error) {
                    console.error('Failed to parse stored user:', error)
                    return null
                }
            }
        }
        console.log('No user found, will collect info')
        return null
    })

    const [isCollectingUserInfo, setIsCollectingUserInfo] = useState(!user)

    // Update collecting state when user changes
    useEffect(() => {
        console.log('User changed:', user)
        console.log('Setting isCollectingUserInfo to:', !user)
        setIsCollectingUserInfo(!user)
    }, [user])
    const [userInfoStep, setUserInfoStep] = useState<"name" | "email" | "complete">("name")
    const [tempUserData, setTempUserData] = useState<Partial<User>>({})
    const [products, setProducts] = useState<Product[]>([])
    const [loadingProducts, setLoadingProducts] = useState(false)
    const [orders, setOrders] = useState<any[]>([])
    const [loadingOrders, setLoadingOrders] = useState(false)
    const [services, setServices] = useState<any[]>([])
    const [loadingServices, setLoadingServices] = useState(false)
    const [bookings, setBookings] = useState<any[]>([])
    const [loadingBookings, setLoadingBookings] = useState(false)

    const { speak } = useVoice({
        apiKey,
        baseUrl,
        voiceConfig: {
            provider: 'openai',
            voice: 'alloy',
            speed: 1.0,
            language: 'en',
            model: 'tts-1-hd'
        },
        onTranscript: () => { },
        enabled: voiceEnabled,
    })

    const apiRef = useRef(new AgentWidgetAPI(baseUrl, apiKey))
    const eventSourceRef = useRef<EventSource | null>(null)
    const pollingRef = useRef<NodeJS.Timeout | null>(null)
    const lastMessageTimeRef = useRef<Date | null>(null)

    // Polling function for human agent messages
    const startPolling = useCallback((convId: string) => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current)
        }

        pollingRef.current = setInterval(async () => {
            try {
                const since = lastMessageTimeRef.current?.toISOString()
                const response = await fetch(`${baseUrl}/api/chat/agents/${agentId}/conversations/${convId}/messages/latest${since ? `?since=${since}` : ''}`)

                if (response.ok) {
                    const newMessages = await response.json()

                    if (newMessages.length > 0) {
                        // Add new messages to chat
                        setMessages(prev => {
                            const existingIds = new Set(prev.map(m => m.id))
                            const messagesToAdd = newMessages
                                .filter((msg: any) => !existingIds.has(msg._id))
                                .map((msg: any) => ({
                                    id: msg._id,
                                    content: msg.content,
                                    role: msg.role,
                                    timestamp: new Date(msg.createdAt),
                                    metadata: msg.metadata
                                }))

                            if (messagesToAdd.length > 0) {
                                // Update last message time
                                const latestTime = new Date(Math.max(...newMessages.map((m: any) => new Date(m.createdAt).getTime())))
                                lastMessageTimeRef.current = latestTime
                            }

                            return [...prev, ...messagesToAdd]
                        })
                    }
                }
            } catch (error) {
                console.error('Polling error:', error)
            }
        }, 2000) // Poll every 2 seconds
    }, [baseUrl, agentId])

    const handleUserInfoCollection = useCallback(
        (input: string) => {
            if (userInfoStep === "name") {
                // Save the name
                setTempUserData((prev) => ({ ...prev, name: input.trim() }))
                setUserInfoStep("email")

                // Random email request prompts
                const emailPrompts = [
                    `Nice to meet you, ${input.trim()}! Could you please share your email address so I can assist you better?`,
                    `Great to connect with you, ${input.trim()}! May I have your email so I can help more effectively?`,
                    `Happy to meet you, ${input.trim()}! What’s the best email I can reach you at?`,
                    `It’s a pleasure, ${input.trim()}! Could you provide your email address so I can continue?`,
                    `Glad to meet you, ${input.trim()}! What email should I use to personalize things for you?`,
                    `Nice having you here, ${input.trim()}! Please share your email so I can assist further.`,
                    `Awesome meeting you, ${input.trim()}! Could you drop your email address for me?`,
                    `Hi ${input.trim()}! Before we continue, may I have your email address?`,
                    `Great to meet you, ${input.trim()}! What email address can I save for you?`,
                    `Thanks, ${input.trim()}! Could you tell me your email so I can provide better assistance?`,
                ]

                const botMessage: Message = {
                    id: Date.now().toString(),
                    content: emailPrompts[Math.floor(Math.random() * emailPrompts.length)],
                    role: "assistant",
                    timestamp: new Date(),
                }

                setMessages((prev) => [...prev, botMessage])
                
                // Speak the message if voice is enabled
                if (voiceEnabled) {
                    setTimeout(() => speak(botMessage.content), 100)
                }
            } else if (userInfoStep === "email") {
                const rawInput = input.trim()
                const emailMatch = rawInput.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
                const email = emailMatch ? emailMatch[0] : undefined

                if (!email) {
                    // Random invalid email responses
                    const invalidEmailPrompts = [
                        "Hmm, I couldn't detect a valid email. Could you please enter a proper email address (e.g., example@mail.com)?",
                        "That doesn’t look like a valid email. Could you try again with something like name@example.com?",
                        "Oops! I couldn’t find a valid email there. Please provide one in the correct format.",
                        "I wasn’t able to read an email from that. Can you type it again (e.g., hello@mail.com)?",
                        "Hmm, seems like that’s not a valid email. Could you share the correct one?",
                        "I think your email might be incomplete. Could you re-enter it for me?",
                        "That doesn’t look right 🤔. Please enter your email in the format: user@mail.com.",
                        "Sorry, I couldn’t detect an email. Can you provide a valid one so we can continue?",
                        "Let’s try again 🙂 Could you enter a proper email address?",
                        "Hmm, I wasn’t able to recognize that as an email. Could you type it again properly?",
                    ]

                    const botMessage: Message = {
                        id: Date.now().toString(),
                        content: invalidEmailPrompts[Math.floor(Math.random() * invalidEmailPrompts.length)],
                        role: "assistant",
                        timestamp: new Date(),
                    }

                    setMessages((prev) => [...prev, botMessage])
                    
                    // Speak the message if voice is enabled
                    if (voiceEnabled) {
                        setTimeout(() => speak(botMessage.content), 100)
                    }
                    return
                }

                // Build final user
                const finalUser: User = {
                    id: `user_${Date.now()}`,
                    name: tempUserData.name || "Anonymous",
                    email,
                }

                // Save to localStorage
                if (typeof window !== "undefined") {
                    const key = `agent_user_${agentId}`
                    const value = JSON.stringify(finalUser)
                    console.log("Saving user to localStorage:", key, value)
                    localStorage.setItem(key, value)

                    const verified = localStorage.getItem(key)
                    console.log("Verified saved value:", verified)
                }

                setUser(finalUser)
                setIsCollectingUserInfo(false)
                setUserInfoStep("complete")

                if (onUserUpdate) {
                    onUserUpdate(finalUser)
                }

                // Random success responses
                const successPrompts = [
                    `Perfect! Thanks ${finalUser.name}. How can I help you today?`,
                    `Great! I’ve saved your email, ${finalUser.name}. What can I do for you?`,
                    `Awesome, ${finalUser.name}! You’re all set. How may I assist you today?`,
                    `Thanks a lot, ${finalUser.name}. What would you like help with?`,
                    `Wonderful! I’ve got your details, ${finalUser.name}. How can I support you now?`,
                    `Perfect, ${finalUser.name}. Ready whenever you are — what’s on your mind?`,
                    `Thanks ${finalUser.name}! What would you like me to do for you today?`,
                    `All set, ${finalUser.name}. How may I assist you further?`,
                    `Great to have your info, ${finalUser.name}. What should we tackle next?`,
                    `Perfectly done, ${finalUser.name}! How can I help you right away?`,
                ]

                const botMessage: Message = {
                    id: Date.now().toString(),
                    content: successPrompts[Math.floor(Math.random() * successPrompts.length)],
                    role: "assistant",
                    timestamp: new Date(),
                }

                setMessages((prev) => [...prev, botMessage])
                
                // Speak the message if voice is enabled
                if (voiceEnabled) {
                    setTimeout(() => speak(botMessage.content), 100)
                }
            }
        },
        [userInfoStep, tempUserData, agentId, onUserUpdate, voiceEnabled, speak]
    )


    const sendMessage = useCallback(
        async (content: string) => {
            if (!content.trim() || isLoading) return

            const userMessage: Message = {
                id: Date.now().toString(),
                content: content.trim(),
                role: "user",
                timestamp: new Date(),
            }

            setMessages((prev) => [...prev, userMessage])

            if (isCollectingUserInfo) {
                handleUserInfoCollection(content.trim())
                return
            }

            setError(null)

            try {
                // Send message to API
                const response = await apiRef.current.sendMessage(
                    agentId,
                    content.trim(),
                    conversationId as string,
                    user || undefined,
                )

                // Update conversation ID if new
                if (!conversationId) {
                    setConversationId(response.conversationId)
                    // Start polling for human agent messages
                    startPolling(response.conversationId)
                }

                // Check if we should skip loading (during handoffs)
                if (response.noLoading) {
                    return
                }

                // Set loading only if not in handoff mode
                setIsLoading(true)

                // Create assistant message placeholder
                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    content: "",
                    role: "assistant",
                    timestamp: new Date(),
                    isStreaming: true,
                }

                setMessages((prev) => [...prev, assistantMessage])

                // Set up SSE for streaming response
                const eventSource = apiRef.current.createEventSource(agentId, response.conversationId)
                eventSourceRef.current = eventSource

                eventSource.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data)

                        if (data.error) {
                            setError(data.error)
                            eventSource.close()
                            setIsLoading(false)
                            return
                        }

                        if (data.done) {
                            // Streaming complete
                            console.log('Streaming done, metadata:', data.metadata)
                            setMessages((prev) => {
                                return prev.map((msg) => {
                                    if (msg.id === assistantMessage.id) {
                                        const updatedMsg = {
                                            ...msg,
                                            isStreaming: false,
                                            metadata: data.metadata,
                                            showEndButton: data.metadata?.showEndButton
                                        }
                                        console.log('Updated message with metadata:', updatedMsg)
                                        return updatedMsg
                                    }
                                    return msg
                                })
                            })

                            // Speak the complete message if voice is enabled
                            if (voiceEnabled) {
                                setTimeout(() => {
                                    setMessages(currentMessages => {
                                        const finalMessage = currentMessages.find(m => m.id === assistantMessage.id)
                                        if (finalMessage?.content) {
                                            speak(finalMessage.content)
                                        }
                                        return currentMessages
                                    })
                                }, 100)
                            }
                            eventSource.close()
                            setIsLoading(false)
                        } else if (data.content) {
                            // Clean command tags from content
                            const cleanContent = data.content
                                .replace(/\[SHOW_PRODUCTS\]/g, '')
                                .replace(/\[SHOW_SERVICES\]/g, '')
                                .replace(/\[SHOW_BOOKING_FORM\]/g, '')
                                .replace(/\[SHOW_CART\]/g, '')
                                .replace(/\[SHOW_PAYMENT_OPTIONS\]/g, '')
                                .replace(/\[SHOW_INVOICE\]/g, '')
                                .replace(/\[REQUEST_FEEDBACK\]/g, '')
                                .replace(/\[END_CONVERSATION_BUTTON\]/g, '')

                            // Update streaming message
                            setMessages((prev) =>
                                prev.map((msg) => {
                                    if (msg.id === assistantMessage.id) {
                                        return { ...msg, content: msg.content + cleanContent }
                                    }
                                    return msg
                                }),
                            )
                        }
                    } catch (err) {
                        console.error("Error parsing SSE data:", err)
                    }
                }

                eventSource.onerror = (err) => {
                    console.error("SSE error:", err)
                    setError("Connection error occurred")
                    eventSource.close()
                    setIsLoading(false)
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to send message")
                setIsLoading(false)

                // Remove the user message on error
                setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id))
            }
        },
        [apiKey, agentId, baseUrl, conversationId, isLoading, user, isCollectingUserInfo, handleUserInfoCollection, voiceEnabled, speak, startPolling],
    )

    const initializeChat = useCallback(() => {
        if (messages.length === 0) {
            let welcomeMessage: Message

            if (user) {
                // 10 variations for returning users
                const greetings = [
                    `Hello ${user.name}! How can I help you today?`,
                    `Welcome back, ${user.name}! What would you like assistance with?`,
                    `Hi ${user.name}, great to see you again. How can I assist?`,
                    `Hey ${user.name}! Need a hand with something?`,
                    `Good to see you, ${user.name}. What’s on your mind today?`,
                    `Hello again ${user.name}! How may I be of service?`,
                    `Hi ${user.name}! Ready to pick up where we left off?`,
                    `Welcome, ${user.name}! What can I do for you right now?`,
                    `Hey ${user.name}, I’m here and ready to help. What’s up?`,
                    `Great to have you here, ${user.name}. How can I support you today?`,
                ]
                const randomGreeting =
                    greetings[Math.floor(Math.random() * greetings.length)]

                welcomeMessage = {
                    id: "welcome",
                    content: randomGreeting,
                    role: "assistant",
                    timestamp: new Date(),
                }
            } else {
                // 10 variations for new users
                const newUserPrompts = [
                    "Hello! I'm here to help you. Could you please tell me your name?",
                    "Hi there! To get started, may I know your name?",
                    "Welcome! I'd love to know your name before we continue.",
                    "Hey! Can you share your name with me so I can assist you better?",
                    "Hi! Before we begin, could you tell me your name?",
                    "Greetings! May I know your name so I can personalize things for you?",
                    "Hello! What should I call you as we chat?",
                    "Hi friend! Can you tell me your name so I can help you better?",
                    "Welcome aboard! Let me know your name to get started.",
                    "Hey there! I'd love to know your name before we continue.",
                ]
                const randomPrompt =
                    newUserPrompts[Math.floor(Math.random() * newUserPrompts.length)]

                welcomeMessage = {
                    id: "welcome",
                    content: randomPrompt,
                    role: "assistant",
                    timestamp: new Date(),
                }
            }

            setMessages([welcomeMessage])
            
            // Speak the welcome message if voice is enabled
            if (voiceEnabled) {
                setTimeout(() => speak(welcomeMessage.content), 500)
            }
        }
    }, [messages.length, user, voiceEnabled, speak])

    const clearMessages = useCallback(() => {
        setMessages([])
        setConversationId(null)
        setError(null)

        if (!initialUser) {
            setUser(null)
            setIsCollectingUserInfo(true)
            setUserInfoStep("name")
            setTempUserData({})

            // Clear user from localStorage
            if (typeof window !== "undefined") {
                localStorage.removeItem(`agent_user_${agentId}`)
            }

            initializeChat()
        }

        if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
        }

        if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
        }
    }, [initialUser, initializeChat])

    const loadProducts = useCallback(async () => {
        setLoadingProducts(true)
        console.log("Loading products for agent:", agentId)
        try {
            const productList = await apiRef.current.getProducts(agentId)
            console.log("Products loaded:", productList)
            setProducts(productList)
        } catch (err) {
            console.error("Failed to load products:", err)
        } finally {
            setLoadingProducts(false)
        }
    }, [agentId])

    const createOrder = useCallback(async (product: Product | null, deliveryAddress?: any, cartData?: any) => {
        if (!user?.name || !user?.email) {
            throw new Error("User info required")
        }

        let currentConversationId = conversationId

        // Create conversation if it doesn't exist
        if (!currentConversationId) {
            try {
                const response = await apiRef.current.sendMessage(
                    agentId,
                    product ? `I want to buy ${product.name}` : "I want to make a purchase",
                    undefined,
                    user
                )
                currentConversationId = response.conversationId
                setConversationId(currentConversationId)
            } catch (error) {
                throw new Error("Failed to create conversation")
            }
        }

        if (cartData) {
            // Cart order
            return await apiRef.current.createCartOrder(
                agentId,
                currentConversationId,
                user.name,
                user.email,
                cartData.amount,
                cartData.currency,
                cartData.itemName,
                cartData.itemType,
                cartData.cartItems
            )
        } else if (product) {
            // Single product order
            return await apiRef.current.createOrder(
                agentId,
                product._id,
                currentConversationId,
                user.name,
                user.email,
                deliveryAddress
            )
        } else {
            throw new Error("Either product or cart data is required")
        }
    }, [agentId, user, conversationId])

    const completePaymentOrder = useCallback(async (orderId: string, paymentReference: string) => {
        return await apiRef.current.completePaymentOrder(agentId, orderId, paymentReference)
    }, [agentId])

    const completePaymentBooking = useCallback(async (bookingId: string, paymentReference: string) => {
        return await apiRef.current.completePaymentBooking(agentId, bookingId, paymentReference)
    }, [agentId])

    const loadOrders = useCallback(async () => {
        if (!user?.email) return

        setLoadingOrders(true)
        try {
            const orderList = await apiRef.current.getOrders(agentId, user.email)
            setOrders(orderList)
        } catch (err) {
            console.error("Failed to load orders:", err)
        } finally {
            setLoadingOrders(false)
        }
    }, [agentId, user?.email])

    return {
        messages,
        isLoading,
        error,
        sendMessage,
        clearMessages,
        initializeChat,
        user,
        isCollectingUserInfo,
        products,
        loadingProducts,
        loadProducts,
        createOrder,
        completePaymentBooking,
        completePaymentOrder,
        conversationId,
        orders,
        loadingOrders,
        loadOrders,
        services,
        loadServices: async () => {
            setLoadingServices(true)
            try {
                const serviceList = await apiRef.current.getServices(agentId)
                console.log("Services loaded:", serviceList)
                setServices(serviceList)
                return serviceList
            } catch (err) {
                console.error("Failed to load services:", err)
                return []
            } finally {
                setLoadingServices(false)
            }
        },
        createBooking: async (bookingData: any) => {
            if (!user?.name || !user?.email) {
                throw new Error("User info required")
            }

            try {
                const response = await fetch(`${baseUrl}/api/chat/agents/${agentId}/bookings?apiKey=${apiKey}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ...bookingData,
                        customerName: user.name,
                        customerEmail: user.email
                    })
                })
                if (!response.ok) {
                    throw new Error(`Failed to create booking: ${response.statusText}`)
                }
                return response.json()
            } catch (error) {
                console.error("Failed to create booking:", error)
                throw error
            }
        },
        bookings,
        loadingBookings,
        loadBookings: async () => {
            if (!user?.email) return []

            setLoadingBookings(true)
            try {
                const response = await fetch(`${baseUrl}/api/chat/agents/${agentId}/bookings?apiKey=${apiKey}&customerEmail=${encodeURIComponent(user.email)}`)
                if (!response.ok) {
                    throw new Error(`Failed to fetch bookings: ${response.statusText}`)
                }
                const bookingList = await response.json()
                console.log("Bookings loaded:", bookingList)
                setBookings(bookingList)
                return bookingList
            } catch (error) {
                console.error("Failed to load bookings:", error)
                setBookings([])
                return []
            } finally {
                setLoadingBookings(false)
            }
        },
        setUserInfo: async (newUser: User) => {
            setUser(newUser)
            if (typeof window !== "undefined") {
                const key = `agent_user_${agentId}`
                localStorage.setItem(key, JSON.stringify(newUser))
            }

            // Send user info to server if conversation exists
            if (conversationId) {
                try {
                    await apiRef.current.setCustomerInfo(agentId, conversationId, newUser)
                } catch (error) {
                    console.error('Failed to sync user info with server:', error)
                }
            }

            if (onUserUpdate) {
                onUserUpdate(newUser)
            }
        },
        endConversation: async () => {
            if (conversationId) {
                try {
                    await apiRef.current.endConversation(agentId, conversationId)
                    console.log('Conversation ended and report sent')

                    // Reset conversation state
                    setMessages([])
                    setConversationId(null)
                    setError(null)

                    // Close event source if active
                    if (eventSourceRef.current) {
                        eventSourceRef.current.close()
                        eventSourceRef.current = null
                    }

                    // Stop polling
                    if (pollingRef.current) {
                        clearInterval(pollingRef.current)
                        pollingRef.current = null
                    }

                    // Initialize new chat
                    setTimeout(() => {
                        initializeChat()
                    }, 100)
                } catch (error) {
                    console.error('Failed to end conversation:', error)
                }
            }
        }
    }
}
